import { Component, ErrorHandler, OnInit, ChangeDetectionStrategy, signal } from '@angular/core'

import { AsiakasService } from '../../_angular/service/asiakas/asiakas.service'
import { DebugService } from '../../_angular/service/debug.service'
import { KirjautunutKayttajaService } from '../../_angular/service/kirjautunut-kayttaja.service'

import { myyjanaAlvVelvollisetTilat } from '@lemon/core-firebase/model/kayttaja'
import { Laskuasetukset, LaskuasetuksetSpostille } from '@lemon/core-firebase/model/lasku'

import { DomSanitizer, SafeUrl } from '@angular/platform-browser'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'

import { LaskuUriService } from '@lemon/core-firebase/service/lasku/lasku-uri.service'
import { FirebaseLemonaid, FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { YtjAsiakasData } from 'app/_jaettu-lemonator/model/asiakas'
import { AsiakasUriService } from 'app/_jaettu-lemonator/service/asiakas-uri.service'
import { lemonShare } from 'app/_jaettu-angular/_rxjs/lemon-share.operator'
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of } from 'rxjs'
import { map, switchMap, tap } from 'rxjs/operators'

const OLETUSMAKSUAIKA = 14

interface NaytettavaSpostipohja {
  pohjanNimenAvain: string
  asetukset: LaskuasetuksetSpostille
}

interface NaytettavaLaskutusprofiili {
  avain: string
  nimi: string
  oletusprofiili: boolean
  logo: SafeUrl | string
  oletusmaksuehto: number
  asetukset: Laskuasetukset
  spostipohjat: NaytettavaSpostipohja[]
}

@Component({
  templateUrl: './lemonaid-laskuasetukset.component.html',
  styleUrls: ['./lemonaid-laskuasetukset.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AsiakkaanLemonaidLaskuasetuksetComponent implements OnInit {
  laskutusprofiilitObservable: Observable<NaytettavaLaskutusprofiili[]>
  valittuLaskutusprofiiliObservable: Observable<NaytettavaLaskutusprofiili | null>
  kirjanpitajaOnDevaajaObservable: Observable<boolean>
  laskutusprofiilitUriObservable: Observable<string>
  laskutusprofiilitEncodedUriObservable: Observable<string>
  alvRekisterissaObservable: Observable<boolean>
  alvRekisterinTilaAvainObservable: Observable<string>
  readonly ladattu = signal(false)

  private _valittuLaskutusprofiiliAvainSubject = new BehaviorSubject<string | null>(null)

  constructor(
    private _errorHandler: ErrorHandler,
    private _asiakasService: AsiakasService,
    private _ladataanService: LadataanService,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _sanitizer: DomSanitizer,
    private _laskuUriService: LaskuUriService,
    private _debugService: DebugService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _firebaseLemonator: FirebaseLemonator,
    private _asiakasUriService: AsiakasUriService,
  ) {}

  ngOnInit() {
    this.kirjanpitajaOnDevaajaObservable = this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaObservable
    this.laskutusprofiilitUriObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      map(asiakas => {
        if (asiakas?.avain) {
          return this._laskuUriService.getLaskuasetuksetCollectionUri(asiakas.avain)
        }
        return ''
      }),
    )
    this.laskutusprofiilitEncodedUriObservable = this.laskutusprofiilitUriObservable.pipe(map(uri => this._debugService.createFirestoreLinkLemonaid(uri)))

    const ytjTiedotObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      switchMap(asiakas => {
        if (!asiakas?.avain) {
          return of<YtjAsiakasData>(null)
        }
        const ytjTiedotUri = this._asiakasUriService.annaYtjTiedotUri(asiakas.avain)
        return this._firebaseLemonator.firestoreDoc<YtjAsiakasData>(ytjTiedotUri).listen()
      }),
      lemonShare(),
    )
    this.alvRekisterissaObservable = ytjTiedotObservable.pipe(map(ytjTiedot => this._onkoAlvRekisterissa(ytjTiedot)))
    this.alvRekisterinTilaAvainObservable = ytjTiedotObservable.pipe(map(ytjTiedot => this._annaAlvRekisterinTilaAvain(ytjTiedot)))

    this.laskutusprofiilitObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      tap(() => {
        this.ladattu.set(false)
        this._valittuLaskutusprofiiliAvainSubject.next(null)
      }),
      switchMap(asiakas => {
        if (!asiakas) {
          return of<NaytettavaLaskutusprofiili[]>([])
        }
        const laskutusprofiilitUri = this._laskuUriService.getLaskuasetuksetCollectionUri(asiakas.avain)
        return this._firebaseLemonaid
          .firestoreCollection<Laskuasetukset>(laskutusprofiilitUri)
          .listenSnapshots()
          .pipe(
            map(snapshots => {
              const laskutusprofiilit: Laskuasetukset[] = snapshots.map(snapshot => ({ ...snapshot.data(), avain: snapshot.id }))
              return this._annaNaytettavatLaskutusprofiilit(laskutusprofiilit, asiakas.avain)
            }),
          )
      }),
      tap(() => {
        this.ladattu.set(true)
      }),
      lemonShare(),
    )

    this.valittuLaskutusprofiiliObservable = combineLatest([this.laskutusprofiilitObservable, this._valittuLaskutusprofiiliAvainSubject]).pipe(
      map(([laskutusprofiilit, valittuAvain]) => {
        const valittu = laskutusprofiilit.find(laskutusprofiili => laskutusprofiili.avain === valittuAvain)
        return valittu ?? laskutusprofiilit[0] ?? null
      }),
    )
  }

  valitseLaskutusprofiili(avain: string) {
    this._valittuLaskutusprofiiliAvainSubject.next(avain)
  }

  private _onkoAlvRekisterissa(ytjTiedot: YtjAsiakasData | null): boolean {
    const rekisterit = ytjTiedot?.rekisterit ?? []
    const rekisteritietoPuuttuu = rekisterit.length === 0
    if (rekisteritietoPuuttuu) {
      return true
    }
    const alvRekisterit = rekisterit.filter(rekisteri => rekisteri.nimi === 'Arvonlisäverovelvollisuus')
    const viimeisinAlvRekisteri = alvRekisterit[alvRekisterit.length - 1]
    if (!viimeisinAlvRekisteri) {
      return false
    }
    return myyjanaAlvVelvollisetTilat.some(tila => tila === viimeisinAlvRekisteri.tila)
  }

  private _annaAlvRekisterinTilaAvain(ytjTiedot: YtjAsiakasData | null): string {
    const rekisteritietoPuuttuu = !ytjTiedot?.rekisterit?.length
    if (rekisteritietoPuuttuu) {
      return 'laskuasetukset.alv-rekisteri-ei-tiedossa'
    }
    return this._onkoAlvRekisterissa(ytjTiedot) ? 'laskuasetukset.alv-rekisteri-kylla' : 'laskuasetukset.alv-rekisteri-ei'
  }

  private _annaNaytettavatLaskutusprofiilit(laskutusprofiilit: Laskuasetukset[], oletusprofiiliAvain: string): NaytettavaLaskutusprofiili[] {
    const naytettavat = laskutusprofiilit.map(laskutusprofiili => {
      let logo: SafeUrl | string = '/assets/noimage.png'
      if (laskutusprofiili.base64Logo) {
        logo = this._sanitizer.bypassSecurityTrustUrl('data:;base64,' + laskutusprofiili.base64Logo)
      }
      const laskujenSpostipohjat = this._annaNaytettavatSpostipohjat('laskuasetukset.pohja-lasku', laskutusprofiili.spostiasetukset)
      const muistutustenSpostipohjat = this._annaNaytettavatSpostipohjat('laskuasetukset.pohja-maksumuistutus', laskutusprofiili.muistutusSpostiasetukset)
      const hyvitystenSpostipohjat = this._annaNaytettavatSpostipohjat('laskuasetukset.pohja-hyvityslasku', laskutusprofiili.hyvitysSpostiasetukset)
      const naytettava: NaytettavaLaskutusprofiili = {
        avain: laskutusprofiili.avain,
        nimi: laskutusprofiili.laskutusprofiilinNimi || laskutusprofiili.nimi,
        oletusprofiili: laskutusprofiili.avain === oletusprofiiliAvain,
        logo,
        oletusmaksuehto: laskutusprofiili.oletusMaksuaikaa ?? OLETUSMAKSUAIKA,
        asetukset: laskutusprofiili,
        spostipohjat: [...laskujenSpostipohjat, ...muistutustenSpostipohjat, ...hyvitystenSpostipohjat],
      }
      return naytettava
    })
    const oletusprofiili = naytettavat.filter(laskutusprofiili => laskutusprofiili.oletusprofiili)
    const muutProfiilit = naytettavat.filter(laskutusprofiili => !laskutusprofiili.oletusprofiili).sort((a, b) => (a.nimi ?? '').localeCompare(b.nimi ?? ''))
    return [...oletusprofiili, ...muutProfiilit]
  }

  private _annaNaytettavatSpostipohjat(pohjanNimenAvain: string, spostiasetuksetKielittain: Laskuasetukset['spostiasetukset'] | undefined): NaytettavaSpostipohja[] {
    if (!spostiasetuksetKielittain) {
      return []
    }
    return Object.values(spostiasetuksetKielittain).map(asetukset => {
      const spostipohja: NaytettavaSpostipohja = { pohjanNimenAvain, asetukset }
      return spostipohja
    })
  }

  async asetaTervetuloaDialoginNakyvyys() {
    this._ladataanService.aloitaLataaminen()
    const asiakas = await firstValueFrom(this._asiakasService.nykyinenAsiakasAvainObservable)

    const asetuksetUri = this._laskuUriService.getLaskuasetuksetUri(asiakas.avain, asiakas.avain)
    const doc = this._firebaseLemonaid.firestoreDocRef<Laskuasetukset>(asetuksetUri)
    this._firebaseLemonaid
      .firestoreRunTransaction(t => {
        return t.get<Laskuasetukset, Laskuasetukset>(doc).then(snapshot => {
          if (snapshot.exists) {
            const asetukset = snapshot.data()
            if (asetukset) {
              t.update(doc, { tervetuloaNahty: !asetukset.tervetuloaNahty })
            }
          }
        })
      })
      .then(() => {
        this._ladataanService.lopetaLataaminen()
      })
      .catch(error => {
        this._errorHandler.handleError(error)
      })
  }
}
